import React, { useState, useEffect } from 'react';
import Form from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { standardRequestWithErrorFlash } from 'shared/utils/request';
import DataEntryModal from '@grnhse/seedling/lib/birch/components/modals/data_entry_modal';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import {
  PrimaryButton,
  SecondaryButton,
} from '@grnhse/seedling/lib/birch/components/buttons';
import { RadioGroupField } from '@grnhse/seedling/lib/birch/components/forms/fields/radio_group_field';
import FeedbackTextField from 'talent_matching/components/bulk_actions/feedback/feedback_textfield';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';
import { Person } from 'talent_matching/types';
import {
  NoPaddingStrongYesIcon as StrongYesIcon,
  NoPaddingYesIcon as YesIcon,
  NoPaddingNoIcon as NoIcon,
  NoPaddingStrongNoIcon as StrongNoIcon,
  NoPaddingNoDecisionIcon as NoDecisionIcon,
} from 'shared/components/scorecard_icons/base_icons';
import styled from 'styled-components';
import Tokens from '@grnhse/seedling/lib/birch/tokens';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import { withLocalePrefix } from 'shared/utils/translation';

const t = withLocalePrefix('talent_matching.modals.feedback');

type Props = {
  isOpen: boolean;
  closeModal: () => void;
  hiringPlanTagsPath: string;
  application: Person;
  onSuccess?: () => void;
};

// This is needed to get the clickable area correct
const StyledRadioItem = styled(Stack)`
  padding: ${Tokens.spacing['2']} ${Tokens.spacing['3']} ${Tokens.spacing['2']}
    ${Tokens.spacing['6']};
  margin: -${Tokens.spacing['2']} -${Tokens.spacing['3']} -${Tokens.spacing[
      '2'
    ]} -${Tokens.spacing['6']};
`;

const IconWrapper = styled(Stack)`
  .no-decision {
    height: 24px;
    width: 24px;
  }
`;

export const candidateRatingOptions = [
  {
    value: 0,
    label: (
      <StyledRadioItem direction="row" spacing={1} alignItems="center">
        <Body>{t('options.no_decision')}</Body>
        <IconWrapper>
          <NoDecisionIcon />
        </IconWrapper>
      </StyledRadioItem>
    ),
  },
  {
    value: 1,
    label: (
      <StyledRadioItem direction="row" spacing={1} alignItems="center">
        <Body>{t('options.definitely_not')}</Body>
        <StrongNoIcon />
      </StyledRadioItem>
    ),
  },
  {
    value: 2,
    label: (
      <StyledRadioItem direction="row" spacing={1} alignItems="center">
        <Body>{t('options.no')}</Body>
        <NoIcon />
      </StyledRadioItem>
    ),
  },
  {
    value: 3,
    label: (
      <StyledRadioItem direction="row" spacing={1} alignItems="center">
        <Body>{t('options.yes')}</Body>
        <YesIcon />
      </StyledRadioItem>
    ),
  },
  {
    value: 4,
    label: (
      <StyledRadioItem direction="row" spacing={1} alignItems="center">
        <Body>{t('options.strong_yes')}</Body>
        <StrongYesIcon />
      </StyledRadioItem>
    ),
  },
];

type FormData = {
  candidate_rating_id: { value: number; label: string };
  notes: string;
  notes_with_tags: string;
};

export const StyledRadioGroup = styled(Stack)`
  .MuiGrid-direction-xs-column
    > .MuiGrid-item
    > .MuiGrid-container
    > .MuiGrid-item {
    border: 1px solid ${FullPalette.GREEN_700};
    border-radius: ${Tokens.spacing['0.5']};
    margin: ${Tokens.spacing['1']} ${Tokens.spacing['1.5']};
    padding: ${Tokens.spacing['2']} ${Tokens.spacing['3']};
  }

  margin-bottom: ${Tokens.spacing['1']};
`;

const FeedbackModal = ({
  isOpen,
  closeModal,
  hiringPlanTagsPath,
  application,
  onSuccess,
}: Props) => {
  const updatePerson = useUpdatePerson();

  const [selectedOption, setSelectedOption] = useState<
    string | number | undefined
  >();
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    setSelectedOption(application.userScorecard?.candidate_rating_id || 0);
    setNote(application.userScorecard?.body || '');
  }, [application.userScorecard]);

  const onSubmit = async (data: FormData) => {
    setSubmitting(true);
    const payload = {
      candidate_rating_id: data.candidate_rating_id,
      notes: data.notes,
      notes_with_tags: data.notes,
    };

    try {
      const response = await standardRequestWithErrorFlash.post(
        application.submitFeedbackPath,
        payload
      );

      const newScorecard = response.data.scorecard;
      newScorecard.is_current_user = true;
      const newScorecards = application.scorecards?.length
        ? [...application.scorecards]
        : [];
      const existingScorecardIndex = newScorecards.findIndex(
        (scorecard) => scorecard.id === newScorecard.id
      );

      if (existingScorecardIndex !== -1) {
        newScorecards[existingScorecardIndex] = newScorecard;
      } else {
        newScorecards.unshift(newScorecard);
      }

      const updatedApplication = {
        ...application,
        userScorecard: newScorecard,
        scorecards: newScorecards,
      };

      updatePerson(updatedApplication);
      onSuccess?.();
      closeModal();
    } finally {
      setSubmitting(false);
    }
  };

  const defaultValues = {
    candidate_rating_id: selectedOption,
    notes: '',
    notes_with_tags: '',
  };

  return (
    <DataEntryModal
      isOpen={isOpen}
      size="medium"
      onDismiss={closeModal}
      ariaLabel={'leave-feedback-modal'}
    >
      <Form onSubmit={onSubmit} defaultValues={defaultValues}>
        <DataEntryModal.Header onClick={closeModal}>
          <span className="pii-blur">
            {t('title', { name: application.fullName })}
          </span>
        </DataEntryModal.Header>
        <DataEntryModal.Body>
          <StyledRadioGroup>
            <RadioGroupField
              name="candidate_rating_id"
              ariaLabel={t('rating')}
              options={candidateRatingOptions}
              direction="row"
            />
            <FeedbackTextField getTagsUrl={hiringPlanTagsPath} value={note} />
          </StyledRadioGroup>
        </DataEntryModal.Body>
        <DataEntryModal.Footer>
          <Grid container spacing={1}>
            <Grid>
              <SecondaryButton onClick={closeModal} disabled={submitting}>
                {t('actions.cancel')}
              </SecondaryButton>
            </Grid>
            <Grid>
              <PrimaryButton type="submit" disabled={submitting}>
                {t('actions.save')}
              </PrimaryButton>
            </Grid>
          </Grid>
        </DataEntryModal.Footer>
      </Form>
    </DataEntryModal>
  );
};

export default FeedbackModal;
